import React from 'react';
import { OverlayView } from '@react-google-maps/api';
import { RouteDetails } from '../../types/fare';
import { formatPrice } from '../../services/fareService';
import { formatDistance, formatDuration } from '../../utils/fareCalculationUtils';
import { getPolylineMidpoint, decodePolyline } from '../../utils/polylineUtils';

interface FareOverlayProps {
  route: RouteDetails;
  routeIndex: number;
  isSelected: boolean;
  onSelect: () => void;
}

export default function FareOverlay({
  route,
  routeIndex,
  isSelected,
  onSelect,
}: FareOverlayProps) {
  if (!route.polyline) return null;

  const path = decodePolyline(route.polyline);
  const midpoint = getPolylineMidpoint(path);

  // Determine fare to display from route's own fare data
  let fareText = '';
  if (route.fare) {
    if (route.fare.mode && route.fare.min && route.fare.max) {
      // Single mode fare (tuk or taxi)
      fareText = `Rs. ${formatPrice(route.fare.min)} - ${formatPrice(route.fare.max)}`;
    } else if (route.fare.modes) {
      // Both modes - show tuk-tuk range as it's cheaper
      fareText = `Rs. ${formatPrice(route.fare.modes.tuk.min)} - ${formatPrice(route.fare.modes.tuk.max)}`;
    }
  }

  const isRecommended = routeIndex === 0;

  return (
    <OverlayView
      position={{ lat: midpoint.lat, lng: midpoint.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        onClick={onSelect}
        className={`
          cursor-pointer transform -translate-x-1/2 -translate-y-1/2
          bg-white rounded-lg shadow-lg border-2 transition-all hover:scale-105
          p-2 min-w-[120px] md:p-3 md:min-w-[180px]
          ${isSelected 
            ? isRecommended 
              ? 'border-green-500 ring-2 ring-green-200' 
              : 'border-blue-500 ring-2 ring-blue-200'
            : 'border-gray-300'
          }
          ${isSelected ? 'z-[1000]' : 'z-[100]'}
        `}
      >
        {/* Route badge */}
        {isRecommended && (
          <div className="text-[10px] md:text-xs bg-green-600 text-white px-1.5 py-0.5 md:px-2 rounded-full font-semibold mb-1 md:mb-2 inline-block">
            Recommended
          </div>
        )}

        {/* Desktop: Route summary - Hidden on mobile */}
        <div className="hidden md:block font-semibold text-gray-900 text-sm mb-1">
          {route.summary}
        </div>

        {/* Compact Stats */}
        <div className="text-[10px] md:text-xs text-gray-600 space-y-0.5">
          {/* Mobile & Desktop: Distance and Duration */}
          <div className="font-medium">{formatDistance(route.distanceKm)} • {formatDuration(route.durationMins)}</div>
          
          {/* Mobile & Desktop: Price */}
          {fareText && <div className="font-semibold text-gray-900 text-[11px] md:text-xs">{fareText}</div>}
        </div>
      </div>
    </OverlayView>
  );
}

